import { motion, useInView, Variants } from "framer-motion";
import { useRef, useMemo } from "react";
import type { ElementType } from "react";
import { cn } from "@/lib/utils";

interface TextRevealProps {
  children: string;
  className?: string;
  as?: "h1" | "h2" | "h3" | "h4" | "p" | "span";
  splitBy?: "word" | "character";
  delay?: number;
  staggerDelay?: number;
  once?: boolean;
}

/**
 * TextReveal - Staggered text animation for premium editorial feel
 * Splits text into words or characters with spring physics
 */
export const TextReveal = ({
  children,
  className,
  as: Component = "span",
  splitBy = "word",
  delay = 0,
  staggerDelay = 0.05,
  once = true,
}: TextRevealProps) => {
  const ref = useRef<HTMLElement>(null);
  const RevealTag = Component as ElementType;
  const isInView = useInView(ref, { once, margin: "-50px" });

  const elements = useMemo(() => {
    if (splitBy === "character") {
      return children.split("").map((char, i) => ({
        text: char === " " ? "\u00A0" : char,
        key: `${char}-${i}`,
      }));
    }
    return children.split(" ").map((word, i) => ({
      text: word,
      key: `${word}-${i}`,
    }));
  }, [children, splitBy]);

  const containerVariants: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: delay,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 20,
      rotateX: -45,
    },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 400,
      },
    },
  };

  return (
    <RevealTag
      ref={ref}
      className={cn("inline-block perspective-1000", className)}
    >
      <motion.span
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        className="inline-flex flex-wrap"
        style={{ perspective: "1000px" }}
      >
        {elements.map(({ text, key }) => (
          <motion.span
            key={key}
            variants={itemVariants}
            className="inline-block preserve-3d origin-bottom"
            style={{ transformStyle: "preserve-3d" }}
          >
            {text}
            {splitBy === "word" && <span>&nbsp;</span>}
          </motion.span>
        ))}
      </motion.span>
    </RevealTag>
  );
};
